// src/components/curated/HotelPickerSheet.jsx
// Bottom sheet (mobile) / centred dialog (desktop) listing hotels for one stay.
// First row is the engine's live-rate hotel when there is one, then the curated shortlist.
import React, { useEffect, useMemo, useState } from "react";
import { X, Star, Check, Loader2 } from "lucide-react";
import { TIERS, TIER_LABEL } from "../../curated/curatedTripsData";
import { inr } from "../../creator/creatorTripEngine";

const ORANGE = "#FF5B15";
const INK = "#0F172A";
const MUTED = "#64748B";
const GREEN = "#059669";
const AMBER = "#B45309";

const TIER_STYLE = {
    budget: { background: "#ECFDF5", color: GREEN },
    comfort: { background: "#E0F2FE", color: "#0369A1" },
    premium: { background: "#F3E8FF", color: "#6B3FA0" },
};

export default function HotelPickerSheet({ open, stay, options = [], liveOptions = [], liveLoading = false, curatorTier, onPick, onClose }) {
    const [tier, setTier] = useState("all");
    const [sort, setSort] = useState("recommended");

    useEffect(() => { if (open) { setTier("all"); setSort("recommended"); } }, [open, stay?.id]);
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
    }, [open, onClose]);

    const groups = useMemo(() => {
        if (!stay) return { instant: [], shortlist: [] };
        const mult = stay.nights * stay.rooms;
        const engineRow = stay.engine && stay.engine.hotel
            ? [{
                code: "__live__", name: stay.engine.name, tier: null, live: true, matched: true,
                rating: stay.engine.hotel.rating || null, area: stay.engine.hotel.address || stay.city,
                meal: stay.engine.hotel.room?.meal_type && stay.engine.hotel.room.meal_type !== "Room_Only" ? "Breakfast" : "Room only",
                total: stay.engine.total, perks: ["Matched to this trip", "Instant booking"],
            }]
            : [];
        // Live API results; drop the one that duplicates the engine's matched hotel
        const engineCode = stay.engine?.hotel?.hotel_code;
        const liveRows = liveOptions.filter((h) => h.hotel_code !== engineCode);
        const shortlist = options.map((h) => ({ ...h, total: h.price_pn * mult }));

        const byTier = (h) => tier === "all" || h.tier === tier || (h.matched && tier === "all");
        const order = (list, curatorFirst) => {
            if (sort === "price") return [...list].sort((a, b) => a.total - b.total);
            if (sort === "rating") return [...list].sort((a, b) => (b.rating || 0) - (a.rating || 0));
            const rank = (h) => (h.matched ? -1 : curatorFirst && h.tier === curatorTier ? 0 : 1);
            return [...list].sort((a, b) => rank(a) - rank(b) || (b.rating || 0) - (a.rating || 0) || a.total - b.total);
        };
        return {
            instant: order([...engineRow, ...liveRows].filter(byTier), false),
            shortlist: order(shortlist.filter(byTier), true),
        };
    }, [stay, options, liveOptions, tier, sort, curatorTier]);

    if (!open || !stay) return null;
    const currentCode = stay.picked ? stay.picked.code : (stay.engine?.hotel ? "__live__" : null);

    const Row = (h) => {
        const on = h.code === currentCode;
        const diff = h.total - stay.total;
        return (
            <button key={h.code} onClick={() => onPick(h.code)} style={{ ...S.row, ...(on ? S.rowOn : null) }} aria-pressed={on}>
                {h.image
                    ? <img src={h.image} alt="" loading="lazy" style={{ ...S.thumb, objectFit: "cover", background: "#F1F5F9" }} onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
                    : <div style={S.thumb} aria-hidden="true">{h.name.split(" ").slice(0, 2).map((w) => w[0]).join("")}</div>}
                <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        <span style={{ fontWeight: 700, fontSize: 14, color: INK }}>{h.name}</span>
                        {h.tier && <span style={{ ...S.tag, ...TIER_STYLE[h.tier] }}>{TIER_LABEL[h.tier]}</span>}
                        {!h.live && h.tier === curatorTier && <span style={{ ...S.tag, background: "#F1F5F9", color: "#475569" }}>Curator's tier</span>}
                    </div>
                    <div style={{ fontSize: 12, color: MUTED, marginTop: 3, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                        {h.rating && <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><Star size={12} fill="#F59E0B" color="#F59E0B" /> {h.rating}</span>}
                        {h.area && <span>{h.area}</span>}
                        {h.meal && <span>{h.meal}</span>}
                    </div>
                    {h.perks?.length > 0 && <div style={{ fontSize: 12, color: "#475569", marginTop: 3 }}>{h.perks.join(" · ")}</div>}
                </div>
                <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <div style={{ fontWeight: 800, fontSize: 14, color: INK }}>{inr(h.total)}{!h.live && <span style={{ fontSize: 10, color: AMBER, marginLeft: 4 }}>est.</span>}</div>
                    {h.price_pn > 0 && <div style={{ fontSize: 11, color: MUTED }}>{inr(h.price_pn)}/night</div>}
                    <div style={{ fontSize: 11, marginTop: 2, color: on ? ORANGE : diff > 0 ? AMBER : GREEN, fontWeight: 600 }}>
                        {on ? <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><Check size={12} /> Selected</span> : diff === 0 ? "same" : `${diff > 0 ? "+" : "−"}${inr(Math.abs(diff))}`}
                    </div>
                </div>
            </button>
        );
    };

    const nothing = !groups.instant.length && !groups.shortlist.length && !liveLoading;

    return (
        <div style={S.bg} className="cp-bg" onClick={onClose} data-leadbot-hide="">
            <div style={S.sheet} className="cp-sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={`Choose a stay in ${stay.city}`}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                    <div>
                        <div style={{ fontWeight: 800, fontSize: 18, color: INK }}>Stay in {stay.city}</div>
                        <div style={{ fontSize: 13, color: MUTED, marginTop: 2 }}>
                            {stay.nights} night{stay.nights > 1 ? "s" : ""} · {stay.rooms} room{stay.rooms > 1 ? "s" : ""} · prices for the whole stay
                        </div>
                    </div>
                    <button onClick={onClose} style={S.close} aria-label="Close"><X size={18} /></button>
                </div>

                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 12 }}>
                    {["all", ...TIERS].map((k) => (
                        <button key={k} onClick={() => setTier(k)} style={{ ...S.chip, ...(tier === k ? S.chipOn : null) }}>
                            {k === "all" ? "All" : TIER_LABEL[k]}
                        </button>
                    ))}
                    <span style={{ flex: 1 }} />
                    <select value={sort} onChange={(e) => setSort(e.target.value)} style={S.select} aria-label="Sort hotels">
                        <option value="recommended">Recommended</option>
                        <option value="price">Lowest price</option>
                        <option value="rating">Top rated</option>
                    </select>
                </div>

                <div style={S.list}>
                    <div style={S.groupHead}>
                        <span>Available now · book instantly</span>
                        {liveLoading && <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: ORANGE }}><Loader2 size={13} className="cp-spin" /> Checking live rates</span>}
                    </div>
                    {groups.instant.map(Row)}
                    {!liveLoading && groups.instant.length === 0 && (
                        <div style={S.emptyNote}>{tier === "all" ? "No live rates for these dates. Pick from our shortlist and we'll confirm it." : `No live ${TIER_LABEL[tier].toLowerCase()} rooms for these dates.`}</div>
                    )}

                    {groups.shortlist.length > 0 && (
                        <>
                            <div style={{ ...S.groupHead, marginTop: 10 }}><span>Curator shortlist · we confirm availability</span></div>
                            {groups.shortlist.map(Row)}
                        </>
                    )}
                    {nothing && <div style={S.emptyNote}>No {TIER_LABEL[tier]?.toLowerCase()} options here yet.</div>}
                </div>
                <div style={{ fontSize: 11, color: MUTED, marginTop: 10, lineHeight: 1.5 }}>
                    Live rates are bookable straight away. Shortlist prices are estimates; our team confirms the room and rate before you pay.
                </div>
            </div>
            <style>{CSS}</style>
        </div>
    );
}

const S = {
    bg: { position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", zIndex: 960, display: "flex", alignItems: "flex-end", justifyContent: "center" },
    sheet: { background: "#fff", width: "100%", maxWidth: 600, borderRadius: "20px 20px 0 0", padding: "18px 16px 20px", boxSizing: "border-box", maxHeight: "88vh", display: "flex", flexDirection: "column", fontFamily: "'Inter', -apple-system, system-ui, sans-serif" },
    close: { width: 36, height: 36, borderRadius: "50%", border: "none", background: "#F1F5F9", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: INK, flexShrink: 0 },
    chip: { border: "1.5px solid #E2E8F0", background: "#fff", borderRadius: 99, padding: "6px 12px", fontSize: 12, fontWeight: 600, color: MUTED, cursor: "pointer", fontFamily: "inherit", minHeight: 34 },
    chipOn: { border: `1.5px solid ${ORANGE}`, color: ORANGE, background: "#FFF4EE" },
    select: { height: 34, border: "1.5px solid #E2E8F0", borderRadius: 10, padding: "0 8px", fontSize: 12, fontWeight: 600, fontFamily: "inherit", color: INK, background: "#fff" },
    list: { display: "flex", flexDirection: "column", gap: 8, marginTop: 12, overflowY: "auto", minHeight: 0, flex: 1, paddingRight: 2 },
    row: { display: "flex", alignItems: "center", gap: 12, width: "100%", border: "1.5px solid #EEF2F7", background: "#fff", borderRadius: 14, padding: "10px 12px", cursor: "pointer", fontFamily: "inherit" },
    rowOn: { border: `1.5px solid ${ORANGE}`, background: "#FFF8F4" },
    thumb: { width: 48, height: 48, borderRadius: 12, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 14, color: "#fff", background: "linear-gradient(135deg, #0EA5E9, #14B8A6)" },
    groupHead: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, fontSize: 11, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase", color: MUTED, padding: "2px 2px 0" },
    emptyNote: { fontSize: 12.5, color: MUTED, background: "#F8FAFC", borderRadius: 10, padding: "10px 12px" },
    tag: { fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 99 },
};

const CSS = `
@keyframes cpSpin{to{transform:rotate(360deg)}}
.cp-spin{animation:cpSpin 1s linear infinite}
@keyframes cpUp{from{transform:translateY(100%)}to{transform:none}}
.cp-sheet{animation:cpUp .24s cubic-bezier(.4,0,.2,1)}
@media (min-width: 640px){ .cp-bg{align-items:center !important} .cp-sheet{border-radius:20px !important;max-height:80vh !important} }
@media (prefers-reduced-motion: reduce){ .cp-sheet{animation:none} }
`;
